import React from 'react';
import { FiBriefcase, FiMapPin } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  InfoBox,
  HelpText
} from '../common/FormStyles';
import { EmploymentInfo } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { EMPLOYMENT_INFO_DOCUMENTS } from '@/config/documentsBySection';

interface EmploymentInfoSectionProps {
  data: Partial<EmploymentInfo>;
  onChange: (data: Partial<EmploymentInfo>) => void;
  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
  readonly?: boolean;
}

export const EmploymentInfoSection: React.FC<EmploymentInfoSectionProps> = ({
  data,
  onChange,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
  readonly = false
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  // Calcul automatique de l'ancienneté totale
  const calculateAnciennete = () => {
    if (data.date_debut) {
      const debut = new Date(data.date_debut);
      const maintenant = new Date();
      const diffMs = maintenant.getTime() - debut.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);

      if (data.anciennete_annees !== years) {
        handleChange('anciennete_annees', years);
      }
      if (data.anciennete_mois !== months) {
        handleChange('anciennete_mois', months);
      }
    }
  };

  React.useEffect(() => {
    calculateAnciennete();
  }, [data.date_debut]);

  return (
    <FormSection>
      <SectionTitle>
        <FiBriefcase />
        Situation Professionnelle
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Renseignez les informations relatives à votre emploi actuel. Ces informations sont
          essentielles pour évaluer votre capacité de paiement.
        </p>
      </InfoBox>

      <SubSectionTitle>Employeur</SubSectionTitle>

      <FormGroup>
        <Label $required>Nom de l'employeur</Label>
        <Input
          type="text"
          value={data.employeur || ''}
          onChange={e => handleChange('employeur', e.target.value)}
          placeholder="Nom de l'entreprise ou de l'administration"
          disabled={readonly}
        />
      </FormGroup>

      <FormGrid>
        <FormGroup>
          <Label $required>Secteur d'activité</Label>
          <Select
            value={data.secteur_activite || ''}
            onChange={e => handleChange('secteur_activite', e.target.value)}
            disabled={readonly}
          >
            <option value="">Sélectionnez...</option>
            <option value="Public/Fonction publique">Public/Fonction publique</option>
            
            <option value="Communication et médias">Communication et médias</option>
            <option value="Informatique et télécommunications">Informatique et télécommunications</option>
            <option value="Conseil et services aux entreprises">Conseil et services aux entreprises</option>
            <option value="Sécurité et gardiennage">Sécurité et gardiennage</option>
            <option value="Administration publique">Administration publique</option>
            <option value="Recherche et développement (R&D)">Recherche et développement (R&D)</option>
            <option value="Technologies de l'information (IT)">Technologies de l'information (IT)</option>
            <option value="Intelligence artificielle et data science">Intelligence artificielle et data science</option>
            <option value="Biotechnologies">Biotechnologies</option>
            <option value="Énergie renouvelable et environnement">Énergie renouvelable et environnement</option>
            <option value="Design, audiovisuel et production numérique">Design, audiovisuel et production numérique</option>
            <option value="Édition, culture et arts">Édition, culture et arts</option>
            <option value="Direction d'entreprises">Direction d'entreprises</option>
            <option value="Gouvernance publique">Gouvernance publique</option>
            <option value="Organisations internationales et ONG">Organisations internationales et ONG</option>
            <option value="Stratégie, innovation et prospective">Stratégie, innovation et prospective</option>
            <option value="Enseignement supérieur et think tanks">Enseignement supérieur et think tanks</option>
            <option value="Autres">Autres</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Téléphone employeur</Label>
          <Input
            type="tel"
            value={data.telephone_employeur || ''}
            onChange={e => handleChange('telephone_employeur', e.target.value)}
            placeholder="+221 X XX XX XX XX"
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      <SubSectionTitle>
        <FiMapPin style={{ fontSize: '18px' }} />
        Adresse de l'employeur
      </SubSectionTitle>

      <FormGroup>
        <Label>Adresse complète</Label>
        <Input
          type="text"
          value={data.adresse_employeur || ''}
          onChange={e => handleChange('adresse_employeur', e.target.value)}
          placeholder="Rue, avenue..."
          disabled={readonly}
        />
      </FormGroup>

      <FormGrid>
        <FormGroup>
          <Label>Quartier</Label>
          <Input
            type="text"
            value={data.quartier_employeur || ''}
            onChange={e => handleChange('quartier_employeur', e.target.value)}
            placeholder="Quartier"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Commune</Label>
          <Select
            value={data.commune_employeur || ''}
            onChange={e => handleChange('commune_employeur', e.target.value)}
            disabled={readonly}
          >
            <option value="">Sélectionnez...</option>
            <option value="Abobo">Abobo</option>
            <option value="Adjamé">Adjamé</option>
            <option value="Attécoubé">Attécoubé</option>
            <option value="Cocody">Cocody</option>
            <option value="Koumassi">Koumassi</option>
            <option value="Marcory">Marcory</option>
            <option value="Plateau">Plateau</option>
            <option value="Port-Bouët">Port-Bouët</option>
            <option value="Treichville">Treichville</option>
            <option value="Yopougon">Yopougon</option>
          </Select>
        </FormGroup>
      </FormGrid>

      <SubSectionTitle>Contrat et Poste</SubSectionTitle>

      <FormGrid>
        <FormGroup>
          <Label $required>Type de contrat</Label>
          <Select
            value={data.type_contrat || ''}
            onChange={e => handleChange('type_contrat', e.target.value)}
            disabled={readonly}
          >
            <option value="">Sélectionnez...</option>
            <option value="CDI">CDI</option>
            <option value="CDD">CDD</option>
            <option value="Fonctionnaire">Fonctionnaire</option>
            <option value="Contractuel État">Contractuel État</option>
            <option value="Stage/Apprentissage">Stage/Apprentissage</option>
            <option value="Journalier">Journalier</option>
            <option value="Autre">Autre</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label $required>Poste occupé</Label>
          <Input
            type="text"
            value={data.poste_occupe || ''}
            onChange={e => handleChange('poste_occupe', e.target.value)}
            placeholder="Ex: Comptable, Technicien..."
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      {/* Champs conditionnels selon le type de contrat */}
      {(data.type_contrat === 'CDI' || data.type_contrat === 'CDD') && (
        <FormGrid>
          <FormGroup>
            <Label $required>Date de début</Label>
            <Input
              type="date"
              value={data.date_debut || ''}
              onChange={e => handleChange('date_debut', e.target.value)}
              disabled={readonly}
            />
          </FormGroup>

          {data.type_contrat === 'CDD' && (
            <FormGroup>
              <Label $required>Date de fin</Label>
              <Input
                type="date"
                value={data.date_fin || ''}
                onChange={e => handleChange('date_fin', e.target.value)}
                disabled={readonly}
              />
              <HelpText>Date de fin du contrat</HelpText>
            </FormGroup>
          )}
        </FormGrid>
      )}

      {data.type_contrat === 'Fonctionnaire' && (
        <FormGrid>
          <FormGroup>
            <Label>Matricule</Label>
            <Input
              type="text"
              value={data.matricule || ''}
              onChange={e => handleChange('matricule', e.target.value)}
              placeholder="Numéro de matricule"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Grade</Label>
            <Input
              type="text"
              value={data.grade || ''}
              onChange={e => handleChange('grade', e.target.value)}
              placeholder="Ex: Attaché, Contrôleur..."
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>
      )}

      <SubSectionTitle>Ancienneté et Expérience</SubSectionTitle>

      <FormGrid>
        <FormGroup>
          <Label>Ancienneté dans l'entreprise</Label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <Input
                type="number"
                min="0"
                value={data.anciennete_annees || ''}
                onChange={e => handleChange('anciennete_annees', parseInt(e.target.value))}
                placeholder="Années"
                disabled={readonly}
              />
              <HelpText>Années</HelpText>
            </div>
            <div>
              <Input
                type="number"
                min="0"
                max="11"
                value={data.anciennete_mois || ''}
                onChange={e => handleChange('anciennete_mois', parseInt(e.target.value))}
                placeholder="Mois"
                disabled={readonly}
              />
              <HelpText>Mois</HelpText>
            </div>
          </div>
        </FormGroup>

        <FormGroup>
          <Label>Expérience professionnelle totale</Label>
          <Input
            type="number"
            min="0"
            value={data.experience_totale_annees || ''}
            onChange={e => handleChange('experience_totale_annees', parseInt(e.target.value))}
            placeholder="Nombre d'années"
            disabled={readonly}
          />
          <HelpText>Total d'années d'expérience (tous emplois confondus)</HelpText>
        </FormGroup>
      </FormGrid>

      {/* Documents justificatifs pour cette section */}
      {onDocumentUpload && onDocumentRemove && (
        <DocumentUpload
          sectionTitle="Informations Professionnelles"
          documents={EMPLOYMENT_INFO_DOCUMENTS}
          uploadedFiles={uploadedDocuments}
          onFileSelect={onDocumentUpload}
          onFileRemove={onDocumentRemove}
          readonly={readonly}
        />
      )}
    </FormSection>
  );
};
